// A small right-click popup menu, themed to match the app's dialogs (modal.ts).
// One menu at a time; it dismisses on item-click, outside-click, Escape, scroll,
// another right-click, or losing focus. Positioned at client (px) coords and
// nudged back on screen if it would overflow the viewport.

const CSS = `
.dt-ctxmenu {
  position: fixed; z-index: 60; min-width: 168px; padding: 5px;
  background: rgba(28,30,38,0.98); border: 1px solid rgba(255,255,255,0.14);
  border-radius: 10px; box-shadow: 0 12px 40px rgba(0,0,0,0.5);
  font: 13px/1.4 system-ui, -apple-system, sans-serif; color: #d7dae6;
  display: flex; flex-direction: column; gap: 1px;
}
.dt-ctxmenu button {
  font: inherit; text-align: left; cursor: pointer; color: inherit;
  background: transparent; border: 0; border-radius: 6px; padding: 7px 10px;
}
.dt-ctxmenu button:hover { background: #424862; }
.dt-ctxmenu button:disabled { color: #757b8c; cursor: default; background: transparent; }
.dt-ctxmenu .sep { height: 1px; margin: 4px 6px; background: rgba(255,255,255,0.10); }
`;

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  const s = document.createElement("style");
  s.textContent = CSS;
  document.head.appendChild(s);
  stylesInjected = true;
}

export interface ContextMenuItem {
  label: string;
  action: () => void;
  disabled?: boolean;
}

let current: { el: HTMLElement; cleanup: () => void } | null = null;

export function isContextMenuOpen(): boolean {
  return current !== null;
}

export function closeContextMenu(): void {
  if (!current) return;
  current.cleanup();
  current.el.remove();
  current = null;
}

// Show a menu at client coords (x,y). A `null` item renders a separator.
export function showContextMenu(
  x: number,
  y: number,
  items: (ContextMenuItem | null)[],
): void {
  injectStyles();
  closeContextMenu();

  const el = document.createElement("div");
  el.className = "dt-ctxmenu";
  for (const item of items) {
    if (!item) {
      const sep = document.createElement("div");
      sep.className = "sep";
      el.appendChild(sep);
      continue;
    }
    const b = document.createElement("button");
    b.textContent = item.label;
    b.disabled = !!item.disabled;
    b.addEventListener("click", () => {
      closeContextMenu();
      item.action();
    });
    el.appendChild(b);
  }
  // Off-screen first so we can measure, then clamp into the viewport.
  el.style.left = "-9999px";
  el.style.top = "-9999px";
  document.body.appendChild(el);
  const r = el.getBoundingClientRect();
  const px = Math.min(x, window.innerWidth - r.width - 6);
  const py = Math.min(y, window.innerHeight - r.height - 6);
  el.style.left = `${Math.max(6, px)}px`;
  el.style.top = `${Math.max(6, py)}px`;

  // Dismiss on anything that should close a transient popup. Outside pointerdown
  // is captured so the same click that dismisses doesn't also act on the canvas.
  const onPointerDown = (e: PointerEvent): void => {
    if (!el.contains(e.target as Node)) closeContextMenu();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      e.stopPropagation();
      closeContextMenu();
    }
  };
  const onScroll = (): void => closeContextMenu();
  // Defer attaching pointerdown so the right-click's own events finish first.
  setTimeout(() => {
    document.addEventListener("pointerdown", onPointerDown, true);
  }, 0);
  document.addEventListener("keydown", onKey, true);
  window.addEventListener("blur", onScroll);
  window.addEventListener("wheel", onScroll, { passive: true });

  current = {
    el,
    cleanup: () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("blur", onScroll);
      window.removeEventListener("wheel", onScroll);
    },
  };
}
